/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
     IOrderTemplateNameDialogResources, LinesAddedToCartDialog,ILinesAddedToCartDialogResources
} from '@msdyn365-commerce/components';
import {
    IItemAddedToCartDialogData, IItemAddedToCartDialogResources, ItemAddedToCartDialog
} from '../../themes/adventureworks/views/components/item-added-to-cart-dialog.component';
import { ILinesAddedToCartDialogCustomResources } from './components/order-template-actions';
import MsDyn365, { AsyncResult, getCatalogId, getUrlSync, ICoreContext, IRequestContext } from '@msdyn365-commerce/core';
import { ICartActionResult } from '@msdyn365-commerce/global-state';
import { convertToString, format, ProductList, ProductListLine } from '@msdyn365-commerce/retail-proxy';
import {
    deleteAsync,
    removeProductListLinesAsync,
    updateProductListLinesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import { CommerceProperty, Customer, FeatureState, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    getPriceForSelectedVariant, getProductUrlSync, ObjectExtensions,
    PriceForSelectedVariantInput,
    IProductInventoryInformation,
    StringExtensions,
    getFeatureState,
    createGetFeatureStateInput,
    ArrayExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { Alert, getTelemetryObject, Heading, IModuleProps, INodeProps, ITelemetryContent, UncontrolledPagination } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FullProduct } from '@msdyn365-commerce/commerce-entities';

import {
    IOrderTemplateActionsProps, IOrderTemplateActionsViewProps, IOrderTemplateLineProps, IOrderTemplateLineViewProps, IOrderTemplateTableHeadingProps,
    OrderTemplateActionsView, OrderTemplateLinesStatusMessage, OrderTemplateLineView, OrderTemplateMobileLineView, OrderTemplateTableHeading
} from './components';
import { ActiveOrderTemplateLinesInput } from './actions/get-order-template';
import { OrderTemplateBulkActions } from './components/bulk-actions';
import { IOrderTemplateData } from './order-template.data';
import { IOrderTemplateProps, IOrderTemplateResources } from './order-template.props.autogenerated';
import { OrderTemplate, OrderTemplateLine, IFullProduct } from '../../themes/adventureworks/views/common/order-templates';
import { IAlternateItems } from '../buybox/components/buybox-components';
import { ConfirmAddedToCartDialog } from './components/add-to-cart-confirmation-dialog-component';
import { IAlternateProduct } from '../../themes/adventureworks/views/common';

export interface IOrderTemplateLineActionMessageState {
    itemKey: string;
    userMessage: string;
    statusCssString: string;
    messageGlyph: string;
    isOpen: boolean;
    isItemAddedToOrderTemplateOpen: boolean;
    isConfirmAddSelectionToBagModalOpen: boolean;
    itemAddedToCartDialogData?: IItemAddedToCartDialogData;
    productSelection: IOrderTemplateLineViewModel[];
    isDetailsDisplayed: boolean;
    detailedProductView?: IOrderTemplateLineViewProps;
    isQuantityDisabledForUpdate?: boolean;
    isConfirmPopupOpen?: boolean;
    isConsentTrue?: boolean;
    isConfirmSuppresPopupOpen?: boolean;
    suppressedMesssage?: string;
    selectedProductIds?: number[];
    isModalOpen?: boolean;
    isSalesAgreementPriceFeatureEnabled?: boolean;
    isPriceMinMaxEnabled?: boolean;
}

export type orderTemplateLinesStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';
type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IOrderTemplateViewProps extends IOrderTemplateProps<IOrderTemplateData> {
    OrderTemplateLines: IModuleProps;
    callbacks?: IOrderTemplateLinesCallbacks;
    status?: orderTemplateLinesStatus;
    statusMessage?: React.ReactNode;
    heading?: React.ReactNode;
    OrderTemplateActions?: INodeProps;
    ProductsTable?: INodeProps;
    ProductsTableRow?: INodeProps;
    ProductsTableHeading?: React.ReactNode;
    ProductDetails?: INodeProps;
    orderLines?: IOrderTemplateLineViewProps[];
    productListLines?: ProductListLine[];
    productsStatusMessage?: React.ReactNode;
    orderActions?: IOrderTemplateActionsViewProps | null;
    resources: IOrderTemplateResources;
    pagination?: React.ReactNode;
    itemAddedToCartDialog?: React.ReactNode;
    bulkOrderLineActions?: React.ReactNode;
    isDetailsDisplayed?: boolean;
    detailedProduct?: IOrderTemplateLineProps;
    detailedProductView?: IOrderTemplateLineViewProps;
    productQuantityView?: React.ReactNode;
    addButton?: React.ReactNode;
    isMobile?: boolean;
    toggleDetailsDisplayed?(props: IOrderTemplateLineProps | undefined): void;
    suppresItemPopUp?: React.ReactNode;
    linesAddedToCartDialog?: React.ReactNode;
}

export interface IOrderTemplateLinesCallbacks {
    updateQuantity(itemKey: string, quantity: number): void;
}

export interface IOrderTemplateLineViewModel extends OrderTemplateLine {
    isChecked: boolean;
    productQuantityView: React.ReactNode;
}

/**
 *
 * OrderTemplateLines component.
 * @extends {React.PureComponent<IOrderTemplateProps<IOrderTemplateData>, IOrderTemplateLineActionMessageState>}
 */
@observer
class OrderTemplateLines extends React.Component<IOrderTemplateProps<IOrderTemplateData>, IOrderTemplateLineActionMessageState> {
    private readonly className: string;

    private readonly telemetryContent?: ITelemetryContent;

    @observable
    private _viewport: GridSize;

    @computed get isMobile(): boolean {
        return (this._viewport === 'xs' || this._viewport === 'sm');
    }

    constructor(props: IOrderTemplateProps<IOrderTemplateData>, state: IOrderTemplateLineActionMessageState) {
        super(props);
        this.className = 'ms-order-template';
        this._viewport = props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._updateViewport = this._updateViewport.bind(this);

        this.state = {
            itemKey: '',
            userMessage: '',
            statusCssString: 'success',
            messageGlyph: 'fas fa-check',
            isOpen: false,
            isItemAddedToOrderTemplateOpen: false,
            isConfirmAddSelectionToBagModalOpen: false,
            productSelection: [],
            isDetailsDisplayed: false,
            isQuantityDisabledForUpdate: false,
            isConfirmPopupOpen: false,
            isConsentTrue: false,
            suppressedMesssage: '',
            isConfirmSuppresPopupOpen: false,
            isSalesAgreementPriceFeatureEnabled: false,
            isPriceMinMaxEnabled: false
        };
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this._updateViewport();

        if (MsDyn365.isBrowser && !this.props.context.request.user.isAuthenticated && !this.props.context.request.params.isEditor) {
            window.location.assign(this.props.context.request.user.signInUrl || '/');
        }
    }

    @action
    public async componentDidMount(): Promise<void> {
        this.setState({});
        const orderTemplate = await this.props.data.orderTemplate;
        this.props.config.heading!.text = orderTemplate?.productList.Name || '';
        this.setState({
            productSelection: orderTemplate?.orderTemplateLines as IOrderTemplateLineViewModel[]
        });

        if (MsDyn365.isBrowser) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
        if(this.props.data.featureState && this.props.data.featureState.result && this.props.data.featureState.result.length > 0)
        {
            const isSalesAgreementPriceFeatureEnabled =  this.props.data.featureState.result?.find(
                featureState => featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
            )?.IsEnabled;
            const isPriceMinMaxEnabled= this.props.data.featureState.result?.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
                )?.IsEnabled;
            this.setState({isSalesAgreementPriceFeatureEnabled: isSalesAgreementPriceFeatureEnabled, isPriceMinMaxEnabled: isPriceMinMaxEnabled});
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public shouldComponentUpdate(nextProps: IOrderTemplateProps<IOrderTemplateData>,
        nextState: IOrderTemplateLineActionMessageState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const { emptyOrderTemplateLinesText, errorGettingOrderTemplateLines } = this.props.resources;
        const { orderTemplate, cart } = this.props.data;
        if (cart.status === 'LOADING' || ObjectExtensions.isNullOrUndefined(orderTemplate) || orderTemplate.status === 'LOADING') {
            const errorProps: IOrderTemplateViewProps = {
                ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(this.className, className)
                },
                status: 'LOADING',
                statusMessage: <OrderTemplateLinesStatusMessage errorType='LOADING' text='Loading...' />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources
            };
            return this.props.renderView(errorProps);
        }

        if (cart.status === 'FAILED' || orderTemplate.status === 'FAILED') {
            const errorProps: IOrderTemplateViewProps = {
                ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(`${this.className}`, className)
                },
                status: 'FAILED',
                statusMessage: <OrderTemplateLinesStatusMessage errorType='FAILED' text={errorGettingOrderTemplateLines} />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources
            };

            return this.props.renderView(errorProps);
        }

        const orderTemplateResult = orderTemplate && orderTemplate.result;
        const orderTemplateLines = orderTemplateResult && orderTemplateResult.orderTemplateLines;

        if (!orderTemplateResult || !orderTemplateLines || orderTemplateLines.length === 0) {
            if (orderTemplateResult && !this._isFirstPage() && MsDyn365.isBrowser) {
                window.location.href = this._getOrderTemplatePageUrl();
                return null;
            }

            const emptyViewProps: IOrderTemplateViewProps = {
                ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
                OrderTemplateLines: {
                    moduleProps: this.props,
                    className: classnames(`${this.className}`, className)
                },
                status: 'EMPTY',
                statusMessage: <OrderTemplateLinesStatusMessage errorType='EMPTY' text={emptyOrderTemplateLinesText} />,
                heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
                resources: this.props.resources,
                orderActions:
                    orderTemplateResult &&
                    this._renderOrderTemplateActions(orderTemplateResult, undefined,this._isChannelMultipleCatalogsFeatureEnabled()),
                bulkOrderLineActions: orderTemplateResult && this._getOrderTemplateLineActions(orderTemplateResult.productList.Id),
                OrderTemplateActions: {
                    className: `${this.className}-action-bar`,
                    tag: 'div'
                }
            };

            return this.props.renderView(emptyViewProps);
        }

        const viewProps: IOrderTemplateViewProps = {
            ...(this.props as IOrderTemplateProps<IOrderTemplateData>),
            OrderTemplateLines: {
                moduleProps: this.props,
                className: classnames(`${this.className}`, className)
            },
            status: 'SUCCESS',
            productsStatusMessage: this.state.isOpen && (
                <div className={`${this.className}-products-status`}>
                    <Alert color={this.state.statusCssString} isOpen={this.state.isOpen} toggle={this._onDismiss}>
                        <span aria-live='assertive'>
                            {this.state.userMessage}
                        </span>
                    </Alert>
                </div>
            ),
            heading: heading && <Heading className={`${this.className}__heading`} {...heading} />,
            OrderTemplateActions: {
                className: `${this.className}-action-bar`,
                tag: 'div'
            },
            ProductsTable: {
                className: `${this.className}__table`,
                tag: 'table',
                role: 'grid'
            },
            ProductsTableRow: {
                className: `${this.className}-table-line`,
                tag: 'tr'
            },
            ProductsTableHeading: this._renderProductListHeader(this.props.resources),
            ProductDetails: {
                className: `${this.className}-table-line__link`,
                tag: 'a'
            },
            orderLines: this._renderProductList(),
            callbacks: { updateQuantity: debounce(this._changeQuantity, 2000) },
            orderActions: this._renderOrderTemplateActions(orderTemplateResult,this.props.data.customerInformation.result, this._isChannelMultipleCatalogsFeatureEnabled()),
            pagination: this._renderPagination(),
            itemAddedToCartDialog: this._renderItemAddedToCartDialog(),
            bulkOrderLineActions: this._getOrderTemplateLineActions(orderTemplateResult.productList.Id),
            isDetailsDisplayed: this.state.isDetailsDisplayed,
            toggleDetailsDisplayed: this._toggleDetailsDisplayed,
            detailedProductView: this.state.detailedProductView,
            isMobile: this.isMobile,
            suppresItemPopUp: this._renderSuppresItemDialog(),
            linesAddedToCartDialog: this._renderLinesAddedToCartDialog()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public onSelect(line: IOrderTemplateLineViewModel, isChecked: boolean): void {
        line.isChecked = isChecked;

        const updated = this.state.productSelection.map(product => {
            if (product.fullProduct?.ProductDetails.ItemId === line.fullProduct?.ProductDetails.ItemId) {
                line.isChecked = isChecked;
            }
            return product;
        }) || [];

        this.setState({
            productSelection: updated
        });
    }

    public onSelectAll(isChecked: boolean): void {
        const updated = this.state.productSelection.map(line => {
            line.isChecked = isChecked;
            return line;
        }) || [];

        this.setState({
            productSelection: updated
        });
    }

    private _renderProductListHeader(resources: IOrderTemplateResources): React.ReactNode {
        const { productImage, productNumberText, productText, productUnitPriceText, productUOMText, productQuantityText, productActionsText, selectAllRowsText, productLineCatalogName } = resources;
        const headers: IOrderTemplateTableHeadingProps = {
             productImage: productImage,
            productNumber: productNumberText,
            productName: productText,
            productUnitPrice: productUnitPriceText,
            productUOM: productUOMText,
            productQuantity: productQuantityText,
            productActions: productActionsText,
            selectAllRowsText,
            lines: this.state.productSelection,
            isMobile: this.isMobile,
            clickHandler: (isChecked: boolean) => {
                this.onSelectAll(isChecked);
            },
            isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled(),
            productLineCatalogName: productLineCatalogName
        };

        return <OrderTemplateTableHeading {...headers} />;
    }

    private _getOrderTemplateLineActions(orderTemplateId: string): React.ReactNode {
        return (
            <OrderTemplateBulkActions
                {...this.props}
                orderTemplateId={orderTemplateId}
                selectedLines={this.state.productSelection.filter(line => line.isChecked)}
                onAddSelectionToBag={this._onAddSelectionToBag}
                onRemoveSelection={this._onRemoveSelection}
                isMinifiedView={this.isMobile}
                isMultipleCatalogFeatureEnabledInHq={this._isChannelMultipleCatalogsFeatureEnabled()}
                telemetryContent={this.telemetryContent}
            />
        );
    }

    private _generateLinesAddedToCartDialogResources(): ILinesAddedToCartDialogCustomResources {
        return {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            linesAddedToCartHeaderItemsOneText: this.props.resources.linesAddedToCartHeaderItemsOneText,
            linesAddedToCartHeaderLinesOneText: this.props.resources.linesAddedToCartHeaderLinesOneText,
            linesAddedToCartHeaderLinesFormatText: this.props.resources.linesAddedToCartHeaderLinesFormatText,
            linesAddedToCartHeaderMessageText: this.props.resources.linesAddedToCartHeaderMessageText,
            confirmBodyText: this.props.config.confirmationPopUpBody!,
            confirmHeaderText: this.props.config.alternateItemPopUpHeader!,
            alternateItemBodyText: this.props.config.alternateItemPopUpBody!,
            alternateItemHeaderText: this.props.config.alternateItemPopUpHeader!
        };
    }

    private _generateItemAddedToCartDialogResources(): IItemAddedToCartDialogResources {
        return {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            itemAddedToCartHeaderItemOneText: this.props.resources.itemAddedToCartHeaderItemOneText,
            itemAddedToCartHeaderItemFormatText: this.props.resources.itemAddedToCartHeaderItemFormatText,
            itemAddedToCartHeaderMessageText: this.props.resources.itemAddedToCartHeaderMessageText,
            freePriceText: this.props.resources.freePriceText,
            originalPriceText: this.props.resources.originalPriceText,
            currentPriceText: this.props.resources.currentPriceText
        };
    }

    private readonly _isChannelMultipleCatalogsFeatureEnabled = (): boolean => {
        let isMultipleCatalogFeatureEnabledInHq = false;
        if (this.props.data.featureState) {
            isMultipleCatalogFeatureEnabledInHq =
                this.props.data.featureState.result?.find(
                    feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                )?.IsEnabled || false;
        }
        return isMultipleCatalogFeatureEnabledInHq;
    };

    private readonly _onRemoveSelection = (): void => {
        const { context, resources } = this.props;
        const { telemetry } = context;
        const { removeSelectedFailureMessage, removeSelectedSuccessMessage } = resources;
        const lines = this.state.productSelection;
        const productListLines = lines && lines.length && lines
            .filter(line => line.isChecked)
            .map(line => line.productListLine);

        if (productListLines && productListLines.length > 0) {
            const productListId = productListLines[0].ProductListId;

            const maxWaitTimeInMilliSeconds: number = 1000;
            removeProductListLinesAsync({ callerContext: context.actionContext }, productListId!, productListLines)
                .then(async () => {
                    this.setState({
                        userMessage: removeSelectedSuccessMessage,
                        statusCssString: 'success',
                        messageGlyph: 'fas fa-check',
                        isOpen: true
                    });
                     // eslint-disable-next-line no-promise-executor-return -- fix
                    await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));
                    window.location.reload();
                })
                .catch(error => {
                    telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                    telemetry.error(error);
                    this.setState({
                        userMessage: removeSelectedFailureMessage,
                        statusCssString: 'success',
                        messageGlyph: 'fas fa-check',
                        isOpen: true
                    });
                });
        }
    };

    private readonly _getOrderTemplatePageUrl = (): string => {
        let url = getUrlSync('ordertemplate', this.props.context.actionContext) || '';
        url = url.includes('?') ? url.substr(0, url.indexOf('?')) : url;
        if (this.props.data.orderTemplate?.result?.productList.Id) {
            return `${url}?id=${this.props.data.orderTemplate.result.productList.Id}`;
        }
        return '';

    };

    private readonly _onAddSelectionToBag = async (isConsent: boolean): Promise<void> => {
        const lines = this.state.productSelection;
        let selectedLines = lines.filter(function(item,idx){
            var isActive = false;
            let isPriceAvailable = false;

            if(item.fullProduct && item.fullProduct.ProductDetails && item.fullProduct.ProductDetails.ExtensionProperties)
            {
                const isActiveProps = item.fullProduct.ProductDetails.ExtensionProperties.filter(x=> x.Key === 'IsActive');
                if(!isActive && isActiveProps && isActiveProps[0] && isActiveProps[0].Value !== undefined)
                {
                    isActive= isActiveProps[0].Value.BooleanValue !== undefined ? isActiveProps[0].Value.BooleanValue : false;
                }
            }
            if(item.fullProduct && item.fullProduct.ProductPrice)
            {
                if(item.fullProduct && item.fullProduct.TradeAgreement && item.fullProduct.TradeAgreement.TradeAgreementPrice !== 0)
                {
                    isPriceAvailable = true;
                }
            }
            if(isActive && isPriceAvailable && item.isChecked)
            {return item;}
            else
            {return false;}
        });
        const productIds: number[] = [];
        if (selectedLines.length > 0) {
            if (!isConsent && selectedLines.find(line => this._inventoryLabel(line.fullProduct, line.productAvailability) === 'Out of stock' && (!line.fullProduct?.AlternateProduct?.serviceResp || !line.fullProduct.AlternateProduct.currentProd || (line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId !== 'Always')))) {
                const excludeItems = selectedLines.filter(line => this._inventoryLabel(line.fullProduct, line.productAvailability) === 'Out of stock' && (!line.fullProduct?.AlternateProduct?.serviceResp || !line.fullProduct.AlternateProduct.currentProd || (line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId !== 'Always')));
                if (excludeItems) {
                    const itemIds = excludeItems.map(ln => ln.fullProduct?.ProductDetails.ItemId);

                    selectedLines = selectedLines.filter(x => !itemIds.includes(x.fullProduct?.ProductDetails.ItemId));
                }
            }
            if (selectedLines && selectedLines.length > 0) {
                for (const productLine of selectedLines) {
                    const itemKey = productLine.fullProduct?.ProductDetails.ItemId ?? '';
                    const product = productLine.fullProduct?.ProductDetails ?? { RecordId: 0, ProductTypeValue: 0, BasePrice: 0, Price: 0, AdjustedPrice: 0 };
                    productIds.push(product.RecordId);
                    const quantity = productLine.productListLine.Quantity ?? 0;
                    const inventLabel = this._inventoryLabel(productLine.fullProduct, productLine.productAvailability);
                    await this._addMultipleItemToBag(product, quantity, itemKey, inventLabel, productLine.fullProduct?.AlternateProduct?.currentProd, productLine.fullProduct?.AlternateProduct?.serviceResp && productLine.fullProduct?.AlternateProduct?.serviceResp[0], true,productLine.fullProduct?.IsOversizedItem,productLine.fullProduct?.IsAlternateOversized);
                }
                this.setState({ selectedProductIds: productIds, isModalOpen: true });
            }

        }
        return Promise.resolve(void (0));
    };

    private _renderOrderTemplateActions(orderTemplate: OrderTemplate,customerInfo: Customer | undefined,  isMultipleCatalogFeatureEnabledInHq: boolean): IOrderTemplateActionsViewProps {
        const { id, typeName, context, resources } = this.props;
        const {
            addTemplateToBagButtonText,
            renameTemplateButtonText,
            deleteTemplateButtonText,
            closeWindowButtonText,
            addToCartFailureMessage,
            addToCartSuccessMessage,
            addToCartProcessMessage,
            orderTemplateErrorMessage
        } = resources;

        const orderTemplateActions: IOrderTemplateActionsProps = {
            data: { orderTemplate, customerInfo },
            id,
            typeName,
            context,
            telemetryContent: this.telemetryContent,
            handlers: {
                onRenameTemplate: this._renameTemplate,
                onRemoveTemplate: this._removeTemplate
            },
            isMultipleCatalogFeatureEnabledInHq,
            renameOrderTemplateDialogStrings: this._generateOrderTemplateNameDialogResources(resources),
            resources: {
                addTemplateToBagButtonText,
                renameTemplateButtonText,
                deleteTemplateButtonText,
                addToCartFailureMessage,
                addToCartSuccessMessage,
                addToCartProcessMessage,
                closeWindowButtonText,
                orderTemplateErrorMessage,
                alternateItemBodyText: this.props.config.alternateItemPopUpBody!,
                alternateItemHeaderText: this.props.config.alternateItemPopUpHeader!,
                confirmBodyText: this.props.config.confirmationPopUpBody!,
                confirmHeaderText: this.props.config.confirmationPopUpHeader!,
                linesAddedToCartDialogResources: this._generateLinesAddedToCartDialogResources()
            }
        };

        return OrderTemplateActionsView(orderTemplateActions);
    }

    private _generateOrderTemplateNameDialogResources(resources: IOrderTemplateResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.renameOrderTemplateTitleText,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateTitle,
            orderTemplateDescription: '',
            defaultOrderTemplateName: resources.defaultOrderTemplateName,
            orderTemplateButtonLabel: resources.renameOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelBtnLabel
        };
    }

    private readonly _getCurrentUrl = (reqContext: IRequestContext): URL => {
        if (MsDyn365.isBrowser) {
            return new URL(window.location.href);
        }
        return new URL(reqContext.url.requestUrl.href);
    };

    /**
     * On close handler.
     */
    private readonly onCloseHandler = () => {
        this.setState({
            itemKey: this.state.itemKey,
            userMessage: this.state.userMessage,
            statusCssString: this.state.statusCssString,
            messageGlyph: this.state.messageGlyph,
            isOpen: this.state.isOpen,
            isItemAddedToOrderTemplateOpen: false
        });
    };

    private readonly _renderItemAddedToCartDialog = (): React.ReactNode | null => {
        if (!this.state.itemAddedToCartDialogData) {
            return null;
        }

        const isMultipleCatalogFeatureEnabledInHq =
            this.props.data.featureState?.result?.find(feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')
                ?.IsEnabled || false;

        return (
            <ItemAddedToCartDialog
                className='msc-item-added-to-cart-dialog'
                id={this.props.id}
                typeName={this.props.typeName}
                context={this.props.context}
                dialogStrings={this._generateItemAddedToCartDialogResources()}
                data={this.state.itemAddedToCartDialogData}
                modalOpen={this.state.isItemAddedToOrderTemplateOpen}
                onClose={this.onCloseHandler}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
            />
        );
    };

    private _isFirstPage(): boolean {
        return this._getCurrentPageNumber() === 0;
    }

    private _getCurrentPageNumber(): number {
        const itemsPerPage = this.props.config.itemsPerPage || 50;
        return this.props.context.request.query && (+this.props.context.request.query.skip / itemsPerPage) || 0;
    }

    private readonly _renderPagination = (): React.ReactNode | null => {
        const { config, context, data, resources } = this.props;
        const orderTemplate = data && data.orderTemplate && data.orderTemplate.result;
        if (!orderTemplate) {
            return null;
        }

        const fullUrl = this._getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 50;
        const currentPageNumber = this._getCurrentPageNumber();
        const skipCount = currentPageNumber * itemsPerPage;
        const totalItems = orderTemplate && orderTemplate.totalLines;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }

        return (
            <UncontrolledPagination
                className={`${this.className}__pagination`}
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
            />
        );
    };

    private _renderProductList(): IOrderTemplateLineViewProps[] {
        const {
            selectOneRowText,
            moreButtonText,
            viewProductDetailsText,
            priceFree,
            originalPriceText,
            currentPriceText,
            addOrderTemplateItemToBagButtonText,
            removeItemFromOrderTemplateButtonText,
            productDimensionTypeColor,
            productDimensionTypeSize,
            productDimensionTypeStyle,
            inputQuantityAriaLabel,
            deleteButtonText,
            incrementButtonAriaLabel,
            decrementButtonAriaLabel,
            unavailableProductErrorMessage,
            outOfStockProductErrorMessage
        } = this.props.resources;

        const { context, telemetry, data } = this.props;
        const { catalogs } = data;
        const { imageSettings } = this.props.config;

        return this.state.productSelection.map((line, index) => {
            let isAlternateItemAvailable = false;
            if(line.fullProduct && line.fullProduct.AlternateProduct && line.fullProduct.AlternateProduct.serviceResp && line.fullProduct.AlternateProduct.serviceResp[0] && (line.fullProduct.AlternateProduct.serviceResp[0].UseAltItemId === 'ItemWhen0'
               || line.fullProduct.AlternateProduct.serviceResp[0].UseAltItemId === 'Always') && line.fullProduct.AlternateProduct.currentProd && line.fullProduct.AlternateProduct.currentProd.productDtl && line.fullProduct.AlternateProduct.currentProd.productAvailibility
               && line.fullProduct.AlternateProduct.currentProd.productAvailibility[0] && line.fullProduct.AlternateProduct.currentProd.productAvailibility[0].StockLevelLabel?.toLowerCase().search('out of stock') === -1)
               {
                isAlternateItemAvailable = true;
               }
            const currentProductProps: IOrderTemplateLineProps = {
                orderTemplateLine: line,
                productListLine: line.productListLine,
                price: line.fullProduct?.ProductPrice,
                error: isAlternateItemAvailable ? '' : line.error,
                shouldDisable: line.shouldDisable && !isAlternateItemAvailable,
                context,
                isQuantityDisabledForUpdate: this.state.isQuantityDisabledForUpdate,
                 productUrl: line.fullProduct
                    ? this._updateProductUrlCatalog(line.fullProduct.ProductDetails, context, line.productListLine.CatalogId)
                    : '',
                imageSettings,
                inventoryInformationLabel: this._inventoryLabel(line.fullProduct, line.productAvailability),
                telemetry,
                moduleId: this.props.id,
                moduleTypeName: this.props.typeName,
                quantity: line.productListLine.Quantity!,
                handlers: {
                    onAddToBag: this._addItemToBag,
                    onRemoveItem: this._removeItemFromOrderTemplateLines,
                    onChangeQuantity: this._changeQuantity,
                    onSelect: this.onSelect.bind(this),
                    updateConfirmPopupState: this._updateConfirmPopupState
                },
                resources: {
                    selectOneRowText,
                    moreButtonText,
                    viewProductDetailsText,
                    productDimensionTypeColor,
                    productDimensionTypeSize,
                    productDimensionTypeStyle,
                    freePriceText: priceFree,
                    originalPriceText,
                    currentPriceText,
                    inputQuantityAriaLabel,
                    removeFromOrderTemplateLinesText: removeItemFromOrderTemplateButtonText,
                    addToBagButtonText: addOrderTemplateItemToBagButtonText,
                    deleteButtonText,
                    incrementButtonAriaLabel,
                    decrementButtonAriaLabel,
                    alterItemBodyText: this.props.config.alternateItemPopUpBody!,
                    alterItemHeaderText: this.props.config.alternateItemPopUpHeader!,
                    confirmBodyText: this.props.config.confirmationPopUpBody!,
                    confirmHeaderText: this.props.config.confirmationPopUpHeader!,
                    unavailableProductErrorMessage,
                    outOfStockProductErrorMessage
                },
                index,
                isMobile: this.isMobile,
                toggleDetailsDisplayed: this._toggleDetailsDisplayed,
                isConfirmPopupOpen: this.state.isConfirmPopupOpen,
                featureState: this.props.data.featureState?.result,
                isMultipleCatalogFeatureEnabledInHq: this._isChannelMultipleCatalogsFeatureEnabled(),
                catalogs: catalogs && catalogs.result
            };

            return OrderTemplateLineView(currentProductProps)!;
        });
    }

    // This method is used to update the product url with the catalogId
    private readonly _updateProductUrlCatalog = (
        productDetails: SimpleProduct,
        context: ICoreContext,
        catalogId: number | undefined
    ): string => {
        const productUrl = getProductUrlSync(productDetails, context.actionContext, undefined);
        if (!StringExtensions.isNullOrWhitespace(productUrl) && catalogId !== undefined) {
            const absoluteUrl = new URL(productUrl, window.location.href);
            absoluteUrl.searchParams.set('catalogid', `${catalogId ?? 0}`);
            return absoluteUrl.href;
        }

        return productUrl;
    };

    private readonly _renderSuppresItemDialog = (): React.ReactNode | null => {
        if (!this.state.isConfirmSuppresPopupOpen) {
            return null;
        }

        return (
            <ConfirmAddedToCartDialog
                className='msc-confirm-added-to-cart'
                modalHeader={this.props.config.alternateItemPopUpHeader!}
                modalBodyText={this.state.suppressedMesssage ?? ''}
                modalOpen={this.state.isConfirmSuppresPopupOpen}
                onClose={this.closeConfirmAddedToCartDialog}
                alternateItem={true}
                _addItemToCartCustom={this.closeConfirmAddedToCartDialog}
                quantity={1}
                onNoClick={this.closeConfirmAddedToCartDialog}
            />
        );
    };

    private readonly _renderLinesAddedToCartDialog = (): React.ReactNode | null => {
        if (!this.state.isModalOpen) {
            return null;
        }
        const { orderTemplate } = this.props.data;
        const dialogStrings: ILinesAddedToCartDialogResources = {
            viewCartButtonText: this.props.resources.viewCartButtonText,
            orderTemplateCloseButtonText: this.props.resources.orderTemplateCloseButtonText,
            linesAddedToCartHeaderItemsOneText: this.props.resources.linesAddedToCartHeaderItemsOneText,
            linesAddedToCartHeaderLinesOneText: this.props.resources.linesAddedToCartHeaderLinesOneText,
            linesAddedToCartHeaderLinesFormatText: this.props.resources.linesAddedToCartHeaderLinesFormatText,
            linesAddedToCartHeaderMessageText: this.props.resources.linesAddedToCartHeaderMessageText

        };


        const existingProductList = orderTemplate?.result?.productList;
        const productList: ProductList = {
            Name: existingProductList?.Name,
            CreatedDateTime: existingProductList?.CreatedDateTime,
            CustomerId: existingProductList?.CustomerId,
            Id: existingProductList?.Id ?? '',
            LastUpdated: existingProductList?.LastUpdated,
            DeletedDateTime: existingProductList?.DeletedDateTime,
            ProductListTypeValue: existingProductList?.ProductListTypeValue,
            DataAreaId: existingProductList?.DataAreaId
        };
        const productLines: ProductListLine[] = [];
        const products: FullProduct[] = [];
        this.state.selectedProductIds?.forEach(id => {
            productLines.push(productList?.ProductListLines?.find(p => p.ProductId === id) ?? {});
            products.push(this.props.data.orderTemplate?.result?.orderTemplateLines.find(ordTemp => ordTemp.fullProduct?.ProductDetails.RecordId === id)?.fullProduct!);
        });
        if (productList) { productList.ProductListLines = productLines; }
        //const products =  this.props.data.orderTemplate?.result?.orderTemplateLines.filter(ordTemp=> ordTemp.fullProduct?.ProductDetails).map(line => line.fullProduct).filter(fullProduct => fullProduct) as FullProduct[];

        return (
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={this.props.id}
                typeName={this.props.typeName}
                context={this.props.context}
                dialogStrings={dialogStrings}
                data={{
                    linesCount: this.state.selectedProductIds?.length ?? 0,
                    orderTemplate: productList,
                    products
                }}
                modalOpen={this.state.isModalOpen}
                onClose={this.closeLineAddedToCartDialog}
            />
        );
    };


    private closeConfirmAddedToCartDialog = () => {
        this.setState({ isConfirmSuppresPopupOpen: false });
    };
    private closeLineAddedToCartDialog = () => {
        this.setState({
            isModalOpen: false,
            itemKey: this.state.itemKey,
            userMessage: this.state.userMessage,
            statusCssString: this.state.statusCssString,
            messageGlyph: this.state.messageGlyph,
            isOpen: this.state.isOpen,
            isItemAddedToOrderTemplateOpen: false
        });

    };
    private readonly _inventoryLabel = (product?: IFullProduct, productAvailability?: IProductInventoryInformation[]): string | undefined => {
        if (productAvailability) {
            const productInventoryInformation = this._getProductInventory(product, productAvailability);
            if (productInventoryInformation) {
                return productInventoryInformation.StockLevelLabel;
            }
        }

        return undefined;
    };

    private _getProductInventory(product?: IFullProduct, productInventoryCollection?: IProductInventoryInformation[] | undefined): IProductInventoryInformation | undefined {
        if (productInventoryCollection && productInventoryCollection.length > 0) {
            for (const productResult of productInventoryCollection) {
                if (product && product.ProductDetails.RecordId === productResult.ProductAvailableQuantity.ProductId) {
                    return productResult;
                }
            }
        }
        return undefined;
    }

    private readonly _toggleDetailsDisplayed = (props: IOrderTemplateLineProps): void => {
        let state = {
            isDetailsDisplayed: !this.state.isDetailsDisplayed
        };
        if (props) {
            state = {
                ...state, ...{
                    detailedProductView: OrderTemplateMobileLineView(props)
                }
            };
        }
        this.setState(state);
    };

    // Dismiss alert when addingToCart/removeItem
    private readonly _onDismiss = () => {
        this.setState({
            isOpen: false
        });
    };

    private readonly _changeQuantity = async (itemKey: string, quantity: number, props?: IOrderTemplateLineProps) => {
        const { context, data, telemetry, config, resources } = this.props;
        const { orderTemplate } = data;

        if (context) {
            if (orderTemplate?.result) {
                const orderTemplateLinesData = orderTemplate.result.orderTemplateLines;
                if (orderTemplateLinesData) {
                    const orderLineIndex = orderTemplateLinesData.findIndex((lines: OrderTemplateLine) => lines.productListLine.LineId === itemKey);
                    const newProductListLine = { ...orderTemplateLinesData[`${orderLineIndex}`] };
                    if (orderTemplateLinesData[`${orderLineIndex}`].productListLine.Quantity === quantity) {
                        return;
                    }
                    const prevQuantity = newProductListLine.productListLine.Quantity;
                    newProductListLine.productListLine.Quantity = quantity;
                    const { fullProduct, ...line } = newProductListLine;

                    // Update mobile modal
                    if (props) {
                        props.quantity = quantity;
                        this.setState({
                            detailedProductView: OrderTemplateMobileLineView(props)
                        });
                    }

                    this.setState({
                        isQuantityDisabledForUpdate: true
                    });

                    updateProductListLinesAsync({ callerContext: context.actionContext }, orderTemplate.result.productList.Id, [line.productListLine])
                        .then(list => {
                            this.setState({
                                itemKey,
                                userMessage: '',
                                statusCssString: 'success',
                                messageGlyph: 'fas fa-check',
                                isQuantityDisabledForUpdate: false,
                                isOpen: false
                            });
                            if (list) {
                                const newLine = list.find(lines => lines.LineId === itemKey);
                                orderTemplateLinesData[`${orderLineIndex}`].productListLine = newLine!;
                                const updatedLines = [...orderTemplateLinesData];
                                updatedLines[`${orderLineIndex}`] = { fullProduct, productListLine: newLine! };
                                const updatedOrderTemplate = { ...orderTemplate.result, ProductListLines: updatedLines };
                                context.actionContext.update(new ActiveOrderTemplateLinesInput(context.actionContext.requestContext, config), updatedOrderTemplate);
                            }
                        })
                        .catch(error => {
                            telemetry.warning('[order-template-lines] Unable to update product quantity.');
                            telemetry.error(error);
                            const userMessage = resources.editOrderTemplateLineQuantityError;
                            if (props) {
                                props && (props.productListLine.Quantity = prevQuantity);
                                this.setState({
                                    itemKey,
                                    userMessage,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isQuantityDisabledForUpdate: false,
                                    isOpen: true,
                                    detailedProductView: OrderTemplateMobileLineView(props)
                                });
                            } else {
                                newProductListLine.productListLine.Quantity = prevQuantity;
                                this.setState({
                                    itemKey,
                                    userMessage,
                                    statusCssString: 'danger',
                                    messageGlyph: 'fas fa-exclamation-triangle',
                                    isQuantityDisabledForUpdate: false,
                                    isOpen: true
                                });
                            }
                        });
                }
            }
        }
    };

    private readonly _addMultipleItemToBag = async (product: SimpleProduct, quantity: number, itemKey: string, inventoryLabel?: string, alternateProd?: IAlternateProduct, alternateItem?: IAlternateItems, ismultiLineCall?: boolean,isOversized?: boolean, isAlternativeOversized?: boolean): Promise<void> => {
        const { context, data, resources, telemetry } = this.props;
        const { cart } = data;
        const { maxQuantityLimitText, addedToCartFailureMessage,invalidQuantityErrorMessageText,customQuantityLimitErrorMessageText} = resources;
        let isChanged: boolean = false;
        if (context) {
            if (alternateItem && alternateItem.UseAltItemId === 'Always' && alternateProd) {
                isChanged = true;
            }
            if (cart.result) {
                const addProductToCartInput = {
                    product,
                    count: quantity
                };
                // product = alternateProd!;
                const addHardSurpresedProductToCartInput = {
                    product: alternateProd?.productDtl ?? product,
                    count: quantity
                };
                if(quantity > context.app.config.cartMaxQuantity)
                {
                    this.setState({
                        itemKey,
                        userMessage: invalidQuantityErrorMessageText.replace('{minQuantity}', context.app.config.cartMaxQuantity),
                        statusCssString: 'danger',
                        messageGlyph: 'fas fa-exclamation-triangle',
                        isOpen: true
                    });
                }
                else
                {
                    const cartLines = cart.result && cart.result.cart && cart.result.cart.CartLines ? cart.result.cart.CartLines.filter(x => x.ItemId === product.ItemId) : undefined;
                    let isValid = true;
                    if(cartLines && cartLines.length>0)
                    {
                        const lines = cartLines.filter(x => x.ItemId === product.ItemId);
                        let sum: number = 0;
                        if (lines !== null || lines !== undefined)
                        {
                            lines.forEach(a => sum += a.Quantity && a.Quantity ? a.Quantity : 0);
                        }
                        const cartLineQty = sum + (quantity && quantity? quantity:0);
                    if((cartLineQty && cartLineQty >= this.props.context.app.config.cartMaxQuantity))
                    {
                        isValid = false;
                        this.setState({
                            itemKey,
                            userMessage: customQuantityLimitErrorMessageText.replace('{minQuantity}', convertToString(sum)).replace('{minQuantity}',this.props.app.config.cartMaxQuantity),
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: false
                        });
                    }
                    }
                    if(isValid === true)
                    {
                 const result = await cart.result
                    .addProductToCart((isChanged ? addHardSurpresedProductToCartInput : addProductToCartInput));
                if (result) {
                    if (result.status === 'SUCCESS') {
                        // if(isBackOrder){
                            let overSized:boolean | undefined = false;
                            let prodOversized: SimpleProduct = product;
                            if(isChanged && isAlternativeOversized)
                            {
                                overSized = isAlternativeOversized;
                                prodOversized = addHardSurpresedProductToCartInput.product;
                            }
                            else
                            {
                                overSized = isOversized;
                            }
                            if(overSized)
                            {
                                    const cartLines = cart.result && cart.result.cart && cart.result.cart.CartLines ? cart.result.cart.CartLines.filter(x => x.ItemId === prodOversized.ItemId) : undefined;
                                    if (cartLines && cartLines.length > 0) {
                                        const extProp: CommerceProperty[] = [];
                                        const existingExtensionProp = cart.result && cart.result.cart && cart.result.cart.ExtensionProperties;
                                        cartLines.forEach(cartLine => {
                                            const existingProp: CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext => ext.Key === cartLine.LineId) : undefined;
                                            if (existingProp === undefined) {
                                                const updatedExistingProp: CommerceProperty = { Key: cartLine.LineId, Value: { StringValue: 'Oversized Item' } };
                                                extProp.push(updatedExistingProp);
                                            }
                                        });
                                        cart?.result?.updateExtensionProperties({ newExtensionProperties: extProp }).then();
                                    }
                            }
                        // }
                        return getPriceForSelectedVariant(
                            new PriceForSelectedVariantInput(
                                product.RecordId,
                                this.props.context.actionContext.requestContext.apiSettings.channelId,
                                product),
                            this.props.context.actionContext).then(priceResult => {
                                if (!priceResult) {
                                    return AsyncResult.reject('Failed to retrieve product price');
                                }
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
                                const isMiniCart = context.app.config.addToCartBehavior === 'showMiniCart';
                                this.setState({
                                    itemKey,
                                    userMessage: '',
                                    statusCssString: 'success',
                                    messageGlyph: 'fas fa-check',
                                    isOpen: false,
                                    isItemAddedToOrderTemplateOpen: !isMiniCart,
                                    isDetailsDisplayed: false,
                                    isConsentTrue: false,
                                    isConfirmPopupOpen: false,
                                    suppressedMesssage: format(this.props.config.alternateItemPopUpBody ?? '', alternateProd?.productDtl?.ItemId ?? ''),
                                    isConfirmSuppresPopupOpen: !ismultiLineCall ? isChanged : false

                                });
                                return AsyncResult.resolve();
                            });
                    } else if (result.substatus === 'MAXQUANTITY') {
                        this.setState({
                            itemKey,
                            userMessage:
                                maxQuantityLimitText
                                    .replace('{curQuantity}', quantity.toString())
                                    .replace('{maxQuantity}', context.app.config.maxQuantityForProductListLineItem.toString()) ||
                                `You can only add ${context.app.config.maxQuantityForProductListLineItem} of this item to your shopping bag`,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        });
                        return AsyncResult.resolve();
                    }
                    telemetry.debug('[order-template-lines] Unable to Update Cart Line from the order template lines');
                    this.setState({
                        itemKey,
                        userMessage: addedToCartFailureMessage,
                        statusCssString: 'danger',
                        messageGlyph: 'fas fa-exclamation-triangle',
                        isOpen: true
                    });
                    return AsyncResult.resolve();
                }
            }
        }
            }
            // }

        }
    };

    private readonly _addItemToBag = (product: SimpleProduct, quantity: number, itemKey: string, inventoryLabel?: string, alternateProd?: IAlternateProduct, alternateItem?: IAlternateItems, isBackOrder?: boolean, isOversized?: boolean, isAlternateOversized?: boolean): void => {
        // const gcBackorderAllow = this.props.data.customerInformation && this.props.data.customerInformation.result?.ExtensionProperties ? this.props.data.customerInformation.result.ExtensionProperties?.find(f=>f.Key==="GCALLOWBACKORDERS")?.Value?.StringValue : '';
        // if ((inventoryLabel && inventoryLabel === 'Out of stock' && (!alternateItem || !alternateProd || (alternateItem && alternateItem.UseAltItemId !== 'Always'))) && !this.state.isConsentTrue && gcBackorderAllow !== AllowBackorder.Yes) {
        //     this.setState({ isConfirmPopupOpen: true });
        // }
        //else {
            const { context, data, resources, telemetry } = this.props;
            const { cart } = data;
            const { maxQuantityLimitText, addedToCartFailureMessage,invalidQuantityErrorMessageText,customQuantityLimitErrorMessageText} = resources;
            let isChanged: boolean = false;
            if (context) {
                if (alternateItem && alternateItem.UseAltItemId === 'Always' && alternateProd) {
                    isChanged = true;
                }
                if (cart.result) {
                    const addProductToCartInput = {
                        product,
                        count: quantity,
                        enableStockCheck: context.app.config.enableStockCheck,
                        availableQuantity: undefined
                    };
                    const addHardSurpresedProductToCartInput = {
                        product: alternateProd?.productDtl ?? product,
                        count: quantity,
                        availableQuantity: alternateProd && alternateProd.productAvailibility && alternateProd.productAvailibility[0].ProductAvailableQuantity && alternateProd.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity ? alternateProd.productAvailibility[0].ProductAvailableQuantity.AvailableQuantity : undefined
                    };
                    if(quantity > context.app.config.cartMaxQuantity)
                    {
                        this.setState({
                            itemKey,
                            userMessage: invalidQuantityErrorMessageText.replace('{minQuantity}', context.app.config.cartMaxQuantity),
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        });
                    }
                    else
                    {
                        const cartLines = cart.result && cart.result.cart && cart.result.cart.CartLines ? cart.result.cart.CartLines.filter(x => x.ItemId === product.ItemId) : undefined;
                        let isValid = true;
                        if(cartLines && cartLines.length>0)
                        {
                            const lines = cartLines.filter(x => x.ItemId === product.ItemId);
                        let sum: number = 0;
                        lines.forEach(a => sum += a.Quantity && a.Quantity ? a.Quantity:0);
                        const cartLineQty = sum + (quantity && quantity? quantity:0);
                        if((cartLineQty && cartLineQty >= this.props.context.app.config.cartMaxQuantity))
                        {
                            isValid = false;
                            this.setState({
                                itemKey,
                                userMessage: customQuantityLimitErrorMessageText.replace('{minQuantity}', convertToString(sum)).replace('{minQuantity}',this.props.app.config.cartMaxQuantity),
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        }
                        }
                        if(isValid === true)
                        {
                    cart.result
                        .addProductToCart((isChanged ? addHardSurpresedProductToCartInput : addProductToCartInput))
                        .then((result: ICartActionResult) => {
                            if (result.status === 'SUCCESS') {
                                // if (isBackOrder) {
                                    let overSized: boolean | undefined=false;
                                    let prodOversized: SimpleProduct = product;
                                    if(isChanged && isAlternateOversized)
                                    {
                                        overSized = isAlternateOversized;
                                        prodOversized = addHardSurpresedProductToCartInput.product;
                                    }
                                    else
                                    {
                                        overSized = isOversized;
                                    }
                                    if(overSized)
                                    {
                                        const cartLines = cart.result && cart.result.cart && cart.result.cart.CartLines && cart.result.cart.CartLines.filter(x => x.ItemId === prodOversized.ItemId);
                                        if (cartLines && cartLines.length > 0) {
                                            const extProp: CommerceProperty[] = [];
                                            const existingExtensionProp = cart.result && cart.result.cart && cart.result.cart.ExtensionProperties;
                                            cartLines.forEach(cartLine => {
                                                const existingProp: CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext => ext.Key === cartLine.LineId) : undefined;
                                                if (existingProp === undefined) {
                                                    const updatedExistingProp: CommerceProperty = { Key: cartLine.LineId, Value: { StringValue: 'Oversized Item' } };
                                                    extProp.push(updatedExistingProp);
                                                }
                                            });
                                            cart?.result?.updateExtensionProperties({ newExtensionProperties: extProp }).then();

                                        }
                                    }
                                // }
                                return getPriceForSelectedVariant(
                                    new PriceForSelectedVariantInput(
                                        product.RecordId,
                                        this.props.context.actionContext.requestContext.apiSettings.channelId,
                                        product),
                                    this.props.context.actionContext).then(priceResult => {
                                        if (!priceResult) {
                                            return AsyncResult.reject('Failed to retrieve product price');
                                        }
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- read config file.
                                        const isMiniCart = context.app.config.addToCartBehavior === 'showMiniCart';
                                        this.setState({
                                            itemKey,
                                            userMessage: '',
                                            statusCssString: 'success',
                                            messageGlyph: 'fas fa-check',
                                            isOpen: false,
                                            isItemAddedToOrderTemplateOpen: !isMiniCart,
                                            itemAddedToCartDialogData: {
                                                product,
                                                productPrice: priceResult,
                                                quantity
                                            },
                                            isDetailsDisplayed: false,
                                            isConsentTrue: false,
                                            isConfirmPopupOpen: false,
                                            suppressedMesssage: format(this.props.config.alternateItemPopUpBody ?? '', alternateProd?.productDtl?.ItemId ?? ''),
                                            isConfirmSuppresPopupOpen: isChanged

                                        });
                                        return AsyncResult.resolve();
                                    });
                            } else {
                            switch (result.substatus) {
                                case 'MAXQUANTITY':
                                    if (context.app.config.maxQuantityForCartLineItem) {
                                        const cartLineQuantity: number | undefined =
                                            cart.result?.cart.CartLines &&
                                            cart.result?.cart.CartLines.find(result => result.ProductId === product.RecordId)?.Quantity;
                                        const defaultMessage = `You can only add ${context.app.config.maxQuantityForCartLineItem} of this item to your shopping bag`;
                                        this.setState({
                                            itemKey,
                                            userMessage:
                                                cartLineQuantity !== undefined
                                                    ? maxQuantityLimitText
                                                          .replace('{curQuantity}', cartLineQuantity.toString())
                                                          .replace(
                                                              '{maxQuantity}',
                                                              context.app.config.maxQuantityForCartLineItem.toString()
                                                          ) || defaultMessage
                                                    : defaultMessage,
                                            statusCssString: 'danger',
                                            messageGlyph: 'fas fa-exclamation-triangle',
                                            isOpen: true
                                        });
                                        return AsyncResult.resolve();
                                    } else {
                                        const defaultMessage = `Max quantity for cart line item is undefined. Please define in configuration settings.`;
                                        this.setState({
                                            itemKey,
                                            userMessage: defaultMessage,
                                            statusCssString: 'danger',
                                            messageGlyph: 'fas fa-exclamation-triangle',
                                            isOpen: true
                                        });
                                        return AsyncResult.resolve();
                                    }
                                case 'INVOICEINCART': {
                                    this.setState({
                                        itemKey,
                                        userMessage: result.errorDetails?.LocalizedMessage || addedToCartFailureMessage,
                                        statusCssString: 'danger',
                                        messageGlyph: 'fas fa-exclamation-triangle',
                                        isOpen: true
                                    });
                                    return AsyncResult.resolve();
                                }
                                default:
                                    telemetry.debug('[order-template-lines] Unable to Update Cart Line from the order template lines');
                                    this.setState({
                                        itemKey,
                                        userMessage: addedToCartFailureMessage,
                                        statusCssString: 'danger',
                                        messageGlyph: 'fas fa-exclamation-triangle',
                                        isOpen: true
                                    });
                                    return AsyncResult.resolve();
                            }
                        }
                        })
                        .catch((error: Error) => {
                           telemetry.warning(error.message);
                        telemetry.debug('[order-template-lines] Error trying to Update Cart Line from the order template lines');
                        if (StringExtensions.isNullOrWhitespace(error.message)) {
                            this.setState({
                                itemKey,
                                userMessage: error.message,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        } else {
                            this.setState({
                                itemKey,
                                userMessage: addedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        }
                        });
                    }
                }
                }
            }
       // }

    };

    private readonly _removeItemFromOrderTemplateLines = async (line: IOrderTemplateLineViewModel) => {
        const { context, data, telemetry } = this.props;
        const { orderTemplate } = data;

        if (context && context.actionContext) {
            const maxWaitTimeInMilliSeconds: number = 1000;
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplate?.result && line) {
                   const featureStates = await getFeatureState(createGetFeatureStateInput(context.actionContext), context.actionContext);
                let isMultipleCatalogFeatureEnabledInHq = false;
                if (ArrayExtensions.hasElements(featureStates)) {
                    isMultipleCatalogFeatureEnabledInHq =
                        featureStates.find(
                            (featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                        )?.IsEnabled || false;
                }
                const catalogId = getCatalogId(context.actionContext.requestContext);
                if (isMultipleCatalogFeatureEnabledInHq) {
                    line.productListLine.CatalogId = catalogId;
                }
                removeProductListLinesAsync({ callerContext: context.actionContext }, line.productListLine.ProductListId!, [line.productListLine])
                    .then(async() => {
                         // eslint-disable-next-line no-promise-executor-return -- fix
                        await new Promise(resolve => setTimeout(resolve, maxWaitTimeInMilliSeconds));
                        window.location.reload();
                    })
                    .catch(error => {
                        telemetry.warning('[order-template-lines] Unable remove items from the order template lines');
                        telemetry.error(error);
                    });
            } else {
                telemetry.warning(
                    '[order-template-lines] Unable remove items from the order template lines user token or lineItemID was not found'
                );
            }
        }
    };

    private readonly _renameTemplate = async (_template: ProductList): Promise<void> => {
        window.location.reload();
        return Promise.resolve();
    };

    private readonly _removeTemplate = () => {
        const { context, data, telemetry, resources } = this.props;
        const { orderTemplate } = data;

        if (context && context.actionContext) {
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken && orderTemplate && orderTemplate.result) {
                deleteAsync({ callerContext: context.actionContext }, orderTemplate.result.productList)
                    .then()
                    .catch((error: string) => {
                        telemetry.warning('[order-template] Unable to delete order template.');
                        telemetry.error(error);
                        this.setState({
                            itemKey: orderTemplate.result?.productList.Id || '',
                            userMessage: resources.deleteOrderTemplateFailedText,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        });
                    });
                const newLink = getUrlSync('ordertemplatelist', this.props.context.actionContext);
                if (newLink) {
                    window.location.assign(newLink);
                }
            } else {
                telemetry.warning('[order-template] Failed to delete order template. Order template was not found');
            }
        } else {
            telemetry.warning('[order-template] Failed to delete order template.');
        }
    };

    private readonly _updateConfirmPopupState = (isOpenPopup: boolean, isConsentGiven?: boolean) => {
        this.setState({ isConfirmPopupOpen: isOpenPopup, isConsentTrue: isConsentGiven });
    };

    private _updateViewport(): void {
        this._viewport = this._getViewport();
    }

    private readonly _getViewport = (): GridSize => {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';

            }
        }

        return 'xs';
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className={`${this.props.config.className}__pagination-left`} aria-hidden='true' />
                <span className='prev-text'>
                    {paginationPreviousText}
                </span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>
                    {paginationNextText}
                </span>
                <span className={`${this.props.config.className}__pagination-right`} aria-hidden='true' />
            </div>
        );
    };
}

export default OrderTemplateLines;
